import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FileUploader } from 'react-drag-drop-files';
import { toast } from 'react-toastify';
import {
    CONTENT_TYPE_ALLOWLIST_BY_PURPOSE,
    MAX_BYTES_BY_PURPOSE,
    fileEnums,
    FindLoad,
    FindSuccessItem,
    Images,
    KeyPurpose,
    previewFile,
    useModal,
    useSecondaryModal,
} from 'utils';
import { fileActions, httpRequestsOnSuccessActions, uploadFile } from 'store';
import { CreateChancel } from '../../buttons';
import { TextRow } from '../tableRows';
import { NotError } from '../../screens';
import { fileTabEnums } from './constants';

const ADD_ACTION_TYPE = 'ADD_FILE';
const EDIT_ACTION_TYPE = 'EDIT_FILE';

/**
 * S3 key purpose per owning model. The purpose decides the S3 prefix, the
 * size cap (MAX_BYTES_BY_PURPOSE) and the content-type allowlist, so it must
 * match the resource the file actually belongs to.
 *
 * Only RESOURCE is mapped off CLINICAL_DOC so far: the other dedicated
 * purposes (REMITTANCE_DOC is pdf/txt only, CLAIM/INVOICE/AUTH/FUNDER/
 * TIMESHEET_DOC are pdf/png/jpg only) are STRICTER on content types than
 * CLINICAL_DOC, so switching them over would start rejecting uploads that
 * work today. Widening those allowlists is a separate pass.
 */
const PURPOSE_BY_MODEL = {
    [fileEnums.RESOURCE]: KeyPurpose.RESOURCE_DOC,
};

const purposeFor = (model) => PURPOSE_BY_MODEL[model] || KeyPurpose.CLINICAL_DOC;

const formatBytes = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * Short display name per content type, used for the "what can I upload" hint
 * and the rejection toast. Anything missing falls back to the MIME subtype, so
 * adding a type to CONTENT_TYPE_ALLOWLIST_BY_PURPOSE never leaves a blank label.
 */
const TYPE_LABELS = {
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/webp': 'WEBP',
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/csv': 'CSV',
    'text/plain': 'TXT',
    'video/mp4': 'MP4',
    'video/webm': 'WEBM',
};

const labelFor = (contentType) => TYPE_LABELS[contentType] || contentType.split('/').pop().toUpperCase();

export const UploadFiles = ({
    onResource,
    currentId,
    defaultValue,
    getUploadedFile,
    singleFile,
    secondaryModal,
    noDescription,
    selectedTab,
    withExpiration, // client documents: optional expiration date (IEPs, immunization records…)
}) => {
    const [error, setError] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadLoader, setUploadLoader] = useState(false);
    const dispatch = useDispatch();
    const addLoader = FindLoad(ADD_ACTION_TYPE);
    const editLoader = FindLoad(EDIT_ACTION_TYPE);
    const success = FindSuccessItem(ADD_ACTION_TYPE);
    const successEdit = FindSuccessItem(EDIT_ACTION_TYPE);
    const { close } = useModal();
    const { closeSecondary } = useSecondaryModal();
    const archived = selectedTab === fileTabEnums.ARCHIVED;

    // Upload policy for this page. Read from the same constants the API
    // enforces, so the picker, the hint text and the server never disagree.
    const purpose = purposeFor(onResource);
    const allowedTypes = CONTENT_TYPE_ALLOWLIST_BY_PURPOSE[purpose] || [];
    const maxBytes = MAX_BYTES_BY_PURPOSE[purpose];
    const allowedLabel = allowedTypes.map(labelFor).join(', ');

    useEffect(() => {
        if (defaultValue) {
            // <input type="date"> wants YYYY-MM-DD
            setUploadedFiles([{ ...defaultValue, expirationDate: defaultValue?.expirationDate?.slice(0, 10) || '' }]);
        }
    }, [defaultValue]);

    useEffect(() => {
        if (success?.type && !defaultValue) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ADD_ACTION_TYPE));
            removeStates();
            close();
            toast(<NotError errMessage={'Document uploaded successfully.'} />);
        }
        if (successEdit?.type && defaultValue) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_ACTION_TYPE));
            removeStates();
            close();
            toast(<NotError errMessage={'Document successfully updated.'} />);
        }
    }, [success, successEdit]);

    const handleUpload = (e) => {
        error === 'currentFile' && setError('');

        if (e) {
            // Checked here rather than in handleSubmit so the user finds out at
            // pick time. `accept` only filters the file-picker dialog; a drag-drop
            // bypasses it entirely, so this is the check that actually holds.
            if (!allowedTypes.includes(e.type)) {
                toast(<NotError type={'error'} errMessage={`"${e?.name}" is not a supported file type. Allowed: ${allowedLabel}.`} />);
                return;
            }

            const pos = e.type.slice(e.type.search('/'));
            const fileType = pos.substring(1);

            const info = {
                file: e,
                name: e?.name,
                description: '',
                format: fileType,
            };
            setUploadedFiles([info, ...uploadedFiles]);
        }
    };

    const handleSubmit = async () => {
        const checkFileName = uploadedFiles?.findIndex((file) => !file.name || file.name.trim() === '');

        if (uploadedFiles?.length && checkFileName === -1) {
            const tooBig = uploadedFiles.find((e) => e?.file?.size > maxBytes);

            if (tooBig) {
                toast(<NotError type={'error'} errMessage={`"${tooBig?.name}" exceeds the ${formatBytes(maxBytes)} upload limit.`} />);
                return;
            }

            setUploadLoader(true);
            try {
                const storedFiles = await Promise.all(uploadedFiles.map((e) => uploadFile(e.file, purpose)));

                const mixedList = storedFiles?.map((file, index) => ({
                    file: {
                        ...file,
                        format: uploadedFiles[index]?.format,
                    },
                    name: uploadedFiles[index]?.name,
                    description: uploadedFiles[index]?.description || '',
                    ...(withExpiration && { expirationDate: uploadedFiles[index]?.expirationDate || null }),
                }));

                const sendList = {
                    documents: mixedList,
                };
                setUploadLoader(false);

                if (onResource === 'getFile') {
                    getUploadedFile(mixedList);

                    if (secondaryModal) {
                        closeSecondary();
                    } else {
                        close();
                    }
                } else {
                    dispatch(
                        fileActions.addFiles(
                            {
                                model: onResource,
                                id: currentId,
                                reqParams: { archived: archived || false },
                            },
                            sendList
                        )
                    );
                }
            } catch (e) {
                toast(<NotError type={'error'} errMessage={e?.data?.message || 'Something went wrong.'} />);
                setUploadLoader(false);
            }
        } else {
            setError(checkFileName);
        }
    };

    const handleEdit = () => {
        const currentFile = uploadedFiles?.find((i) => i?.id === defaultValue?.id);
        if (currentFile?.name && defaultValue) {
            const sendFile = {
                name: currentFile?.name,
                description: currentFile?.description,
                ...(withExpiration && { expirationDate: currentFile?.expirationDate || null }),
                id: defaultValue?.id,
                file: {
                    ...defaultValue?.file,
                },
            };
            dispatch(
                fileActions.editFile(
                    {
                        model: onResource,
                        id: currentId,
                        reqParams: { archived: archived || false },
                    },
                    defaultValue?.id,
                    sendFile
                )
            );
        } else {
            setError(0);
        }
    };

    const removeStates = () => {
        setError('');
    };

    const renderSize = (item) => {
        const bytes = item?.size;
        const KB = 1024;
        const MB = KB * 1024;

        if (bytes >= MB) {
            return (bytes / MB).toFixed(2) + ' MB';
        } else if (bytes >= KB) {
            return (bytes / KB).toFixed(2) + ' KB';
        } else {
            return bytes + ' bytes';
        }
    };

    const removeFile = (item, key) => {
        const updatedItems = uploadedFiles?.filter((item, k) => k !== key);
        setUploadedFiles(updatedItems);
    };

    const changeFileInfo = (e, index) => {
        if (error === index) setError('');

        setUploadedFiles((prev) => prev.map((file, i) => (i === index ? { ...file, [e.target.name]: e.target.value } : file)));
    };

    const checkSingleFile = singleFile ? uploadedFiles?.length !== 1 : !defaultValue;

    return (
        <div style={{ width: 400 }}>
            <div>
                <p className="modal-header-title">{defaultValue ? 'Edit Document' : 'Upload File'}</p>
                <p className="select-document-desc">
                    {defaultValue ? "Edit the document's title or description." : 'Select a file to upload. Details can be added afterward'}
                </p>
            </div>

            <div>
                {checkSingleFile && (
                    <div style={uploadedFiles?.length > 0 ? {} : { border: 'none' }} className="upload-file-header-box">
                        <FileUploader handleChange={handleUpload} name="file">
                            <div className="drag-drop-component">
                                <div className="upload-dock-icon">
                                    <img src={Images.uploadDockCloud} alt="upload" style={{ margin: 0 }} />
                                </div>
                                <label className="custom-file-upload">
                                    <input
                                        onClick={(event) => (event.target.value = null)}
                                        onChange={handleUpload}
                                        type="file"
                                        id="file"
                                        accept={allowedTypes.join(',')}
                                    />
                                    <i className="fa fa-cloud-upload" />
                                    <div className="upload">
                                        <p>
                                            <span style={{ fontWeight: 600 }}>Drag & drop</span>
                                            <span style={{ margin: '0 4px' }}>your file here or</span>
                                            <span style={{ color: '#2E6DD8', fontWeight: 600 }}>click to upload</span>
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#667085' }}>
                                            {allowedLabel} &middot; up to {formatBytes(maxBytes)}
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </FileUploader>
                    </div>
                )}

                {!!uploadedFiles?.length > 0 && (
                    <div className="uploaded-files-wrapper">
                        {uploadedFiles.map((i, j) => (
                            <div
                                className="loaded-documents-box"
                                key={j}
                                style={{
                                    margin: '0 0 8px 0',
                                    flexDirection: 'column',
                                    borderColor: error === j ? '#FE7070' : '#E4E7EC',
                                }}
                            >
                                <div className="uploaded-file-info-header">
                                    <div className="space-between">
                                        <div className="flex-align-center" style={{ gap: 8, width: '90%' }}>
                                            <img src={Images.uploadedFile} alt="icon" />
                                            <p className="loaded-document-name full-width">
                                                <TextRow name={i?.name} fontWeight={600} textWidth={10} />
                                            </p>
                                        </div>
                                        {defaultValue && (i?.file?.key || i?.file?.url) && (
                                            <button
                                                type="button"
                                                className="reset-btn"
                                                style={{ padding: 0, width: '24px', height: 'auto' }}
                                                onClick={() => previewFile(i?.file)}
                                            >
                                                <img style={{ height: 20 }} src={Images.showEye} alt="view" />
                                            </button>
                                        )}
                                        {!defaultValue && (
                                            <button className="reset-btn-by-default" onClick={() => removeFile(i, j)}>
                                                <img src={Images.redTrashLine} alt="icon" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="uploaded-file-detail-box">
                                        {!defaultValue && <p>{renderSize(i?.file)}</p>}
                                        {!defaultValue && <div className="loaded-circle" />}
                                        {(i?.file?.type || i?.file?.format) && (
                                            <p>{(i?.file?.type || i?.file?.format)?.replace('image/', '')}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="full-width">
                                    <div className="uploaded-file-name-input-wrapper">
                                        <p>File name*</p>
                                        <input
                                            onChange={(e) => changeFileInfo(e, j)}
                                            value={i?.name}
                                            type="text"
                                            name={'name'}
                                            placeholder={'File Name*'}
                                            maxLength={50}
                                        />
                                    </div>
                                    {withExpiration && (
                                        <div className="uploaded-file-name-input-wrapper">
                                            <p>Expiration date</p>
                                            <input
                                                onChange={(e) => changeFileInfo(e, j)}
                                                value={i?.expirationDate || ''}
                                                type="date"
                                                name={'expirationDate'}
                                            />
                                        </div>
                                    )}
                                    {!noDescription && (
                                        <div className="uploaded-file-desc-input-wrapper">
                                            <textarea
                                                onChange={(e) => changeFileInfo(e, j)}
                                                value={i?.description}
                                                name={'description'}
                                                placeholder={'File Description'}
                                                maxLength={100}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <CreateChancel
                loader={uploadLoader || !!addLoader?.length || !!editLoader?.length}
                create={defaultValue ? 'Save' : 'Upload'}
                chancel={'Cancel'}
                onCreate={defaultValue ? handleEdit : handleSubmit}
                onClose={secondaryModal ? closeSecondary : close}
                buttonWidth="223px"
                createButnMargin={'16px'}
                disabled={defaultValue ? false : !uploadedFiles?.length}
            />
        </div>
    );
};
